import { t } from 'i18next'
import React from 'react'
import { fromJSX, Link, NavLink, StandardLocalWidget } from 'widgets-for-react'
import { getCookie, setCookie } from '../../cms/blocks/cookiebar.utils.mod'
import { isLinkExternal } from '../../cms/blocks/utils.gen'
import { switchLanguageDatalayer } from '../../datalayer'
import { Locale } from '../../drupal/types_and_parser/extra_types.mod'
import { clickoutside } from '../../helpers'
import { convertEmToPx } from '../utils/convertEmToPx'
import {
  addLangToUrl,
  defaultLanguage,
  getLangFromUrl,
  localeTranslations,
  oldSites,
  updateUrlLang
} from '../utils/history.mod'
import StickyHeader from '../utils/stickyHeader'
import { HeaderState } from './state'

export const headerWidget =
  (isTransparent: boolean, refrescoBranding: boolean): StandardLocalWidget<HeaderState> =>
  (state) =>
    fromJSX((setState) => {
      const isMenuOpen: boolean = window.innerWidth > convertEmToPx(64) ? true : state.mode === 'open' ? true : false
      const toggleCountriesNav: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void = (_) => {
        setState((s) => ({
          ...s,
          countriesNav: s.countriesNav == 'open' ? 'closed' : 'open',
          hoveredPrimMenuItem: null,
          hoveredSecMenuItem: null
        }))
        if (document.documentElement.classList.contains('disable-scroll')) {
          document.documentElement.classList.remove('disable-scroll')
        } else {
          document.documentElement.classList.add('disable-scroll')
        }
      }
      const toggleSearchNav: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void = (_) =>
        setState((s) => ({
          ...s,
          searchNav: s.searchNav == 'open' ? 'closed' : 'open',
          hoveredPrimMenuItem: null,
          hoveredSecMenuItem: null
        }))

      const closeFlyout = () => {
        setState((s1) => ({
          ...s1,
          hoveredPrimMenuItem: undefined,
          hoveredSecMenuItem: undefined,
          hoveredTretMenuItem: undefined
        }))
      }

      const header = document.querySelector('#io-header') as HTMLElement

      if (header) {
        clickoutside(header, () => {
          document.documentElement.classList.remove('nav--toggled')
        })
      }

      const getLinkUrl = (url: string) => {
        // always return a string path for the `to` prop.
        // External links are handled via target (`_blank`) by getLinkTarget.
        if (isLinkExternal(url)) return url
        return updateUrlLang(url, getLangFromUrl())
      }

      const getLinkTarget = (url: string) => {
        if (isLinkExternal(url)) return '_blank'
        return '_self'
      }

      // const cookieData = getCookie('locale')
      const regionCookie = getCookie('selectedRegion')

      return refrescoBranding ? (
        <></>
      ) : (
        <>
          {/* A11y Skiplinks */}
          <a href="#a11y-main" className="sr-text">
            {t('Header:A11y.SkipToContent')}
          </a>
          <a href="#a11y-footer" className="sr-text">
            {t('Header:A11y.SkipToFooter')}
          </a>
          <StickyHeader
            className={`header ${isTransparent ? 'header--transparent' : ''}`}
            innerClassName="header__wrapper"
            scrollingUp="header--scrolling-up"
            scrollingDown="header--scrolling-down"
            pinned="header--pinned"
            onMouseLeave={closeFlyout}
          >
            <div className="header__inner container container--m grid--gutter">
              <Link to={addLangToUrl('/')} className="header__logo" onClick={closeFlyout}>
                <span className="sr-text">{t('Header:ReturnToHome')}</span>
                <img
                  className="logo"
                  src="/images/logo-new-refresco.svg"
                  alt=""
                  width="227"
                  height="77"
                />
              </Link>
              <nav
                className="header__nav nav nav--main nav--mobile"
                id="a11y-main-menu-collapse"
                aria-hidden={!isMenuOpen}
              >
                {state.menuHeader.navLinks.kind == 'loaded' ? (
                  // Primary
                  <ul className="nav__list nav__list--primary">
                    {state.menuHeader.navLinks.value
                      .map((primaryItem) => (
                        <li className="nav__item" key={`main-nav-${primaryItem.url}`}>
                          {primaryItem.children ? (
                            <>
                              <Link
                                className={`nav__link${
                                  state.hoveredPrimMenuItem == primaryItem ? ' nav__link--active' : ''
                                }`}
                                onClick={(_) => {
                                  document.documentElement.classList.remove('nav--toggled')
                                  closeFlyout()
                                }}
                                to={getLinkUrl(primaryItem.url)}
                                target={getLinkTarget(primaryItem.url)}
                              >
                                {primaryItem.label}
                              </Link>
                              <button
                                className={`nav__button${
                                  state.hoveredPrimMenuItem == primaryItem ? ' nav__button--toggled' : ''
                                }`}
                                onClick={(_) =>
                                  setState((s1) => ({
                                    ...s1,
                                    hoveredPrimMenuItem:
                                      state.hoveredPrimMenuItem == primaryItem ? undefined : primaryItem
                                  }))
                                }
                              ></button>
                              {/* Secondary */}
                              <ul className={`sublist sublist--${state.hoveredPrimMenuItem ? 'shown' : 'hidden'}`}>
                                {state.hoveredPrimMenuItem == primaryItem &&
                                  primaryItem.children
                                    .map((secondaryItem) => (
                                      <li
                                        className="sublist__item"
                                        onClick={(event) => {
                                          event.stopPropagation()
                                          setState((s1) => ({ ...s1, hoveredSecMenuItem: secondaryItem }))
                                        }}
                                      >
                                        <Link
                                          onClick={(_) => {
                                            document.documentElement.classList.remove('nav--toggled')
                                            closeFlyout()
                                          }}
                                          to={getLinkUrl(secondaryItem.url)}
                                          target={getLinkTarget(secondaryItem.url)}
                                          className="sublist__link"
                                        >
                                          {secondaryItem.label}
                                        </Link>
                                      </li>
                                    ))
                                    .toArray()}
                              </ul>
                            </>
                          ) : (
                            <>
                              <NavLink
                                className="nav__link"
                                activeClassName="nav__link--active"
                                to={getLinkUrl(primaryItem.url)}
                                target={getLinkTarget(primaryItem.url)}
                                onClick={closeFlyout}
                              >
                                {primaryItem.label}
                              </NavLink>
                            </>
                          )}
                        </li>
                      ))
                      .toArray()}
                    {/* The inline contact link has no room next to the logo on the smallest
                        screens, so the menu carries it there instead. */}
                    <li className="nav__item nav__item--contact">
                      <Link
                        className="nav__link"
                        to={`/${getLangFromUrl()}/${t('header:contact_url')}`}
                        onClick={(_) => {
                          document.documentElement.classList.remove('nav--toggled')
                          closeFlyout()
                        }}
                      >
                        {t('header:contact')}
                      </Link>
                    </li>
                  </ul>
                ) : (
                  <>&nbsp;</>
                )}
              </nav>

              <nav className="header__nav nav nav--main nav--desktop" id="a11y-main-menu">
                {state.menuHeader.navLinks.kind == 'loaded' ? (
                  <ul className="nav__list nav__list--primary">
                    {/* Primary */}
                    {state.menuHeader.navLinks.value
                      .map((primaryItem, primaryIndex) => (
                        <li
                          className="nav__item"
                          key={`main-nav-${primaryItem.url}`}
                          onMouseEnter={(_) =>
                            setState((s1) => ({
                              ...s1,
                              hoveredPrimMenuItem: primaryItem,
                              hoveredSecMenuItem: null
                            }))
                          }
                        >
                          <NavLink
                            className="nav__link"
                            activeClassName="nav__link--active"
                            onClick={closeFlyout}
                            to={getLinkUrl(primaryItem.url)}
                            target={getLinkTarget(primaryItem.url)}
                          >
                            {primaryItem.label}
                            {primaryItem?.children?.count() > 0 && (
                              <svg className="nav__chevron" aria-hidden="true" height="8" width="13">
                                <use xlinkHref="#chevron--down--new" />
                              </svg>
                            )}
                          </NavLink>
                          {primaryItem?.children?.count() > 0 && (
                            <ul className={`sublist`}>
                              {primaryItem.children
                                .map((secondaryItem) => (
                                  <li
                                    className="sublist__item"
                                    onClick={(event) => {
                                      setState((s1) => ({ ...s1, hoveredSecMenuItem: secondaryItem }))
                                    }}
                                    onMouseEnter={(_) =>
                                      setState((s1) => ({ ...s1, hoveredSecMenuItem: secondaryItem }))
                                    }
                                  >
                                    <Link
                                      onClick={(_) => {
                                        document.documentElement.classList.remove('nav--toggled')
                                        closeFlyout()
                                      }}
                                      to={getLinkUrl(secondaryItem.url)}
                                      target={getLinkTarget(secondaryItem.url)}
                                      className="sublist__link"
                                    >
                                      {secondaryItem.label}
                                      {secondaryItem?.children?.count() > 0 && (
                                        <svg className="nav__chevron" aria-hidden="true" height="8" width="13">
                                          <use xlinkHref="#chevron--down--new" />
                                        </svg>
                                      )}
                                    </Link>
                                    {secondaryItem?.children?.count() > 0 && (
                                      <ul className={`sublist sublist--tertiary`}>
                                        {secondaryItem.children
                                          .map((tretMenuItem) => (
                                            <li className="sublist__item">
                                              <Link
                                                className="sublist__link"
                                                to={getLinkUrl(tretMenuItem.url)}
                                                target={getLinkTarget(tretMenuItem.url)}
                                                onClick={closeFlyout}
                                              >
                                                {tretMenuItem.label}
                                              </Link>
                                            </li>
                                          ))
                                          .toArray()}
                                      </ul>
                                    )}
                                  </li>
                                ))
                                .toArray()}
                            </ul>
                          )}
                        </li>
                      ))
                      .toArray()}
                  </ul>
                ) : (
                  <>&nbsp;</>
                )}
              </nav>
              <div className="header__actions">
                <Link className="header__contact" to={`/${getLangFromUrl()}/${t('header:contact_url')}`}>
                  {t('header:contact')}
                </Link>

                <button className="header__lang" onClick={toggleCountriesNav}>
                  <svg className="header__lang-icon" height="22" width="22">
                    <use xlinkHref="#icon--lang" />
                  </svg>
                  <span className="sr-text">{t('header:international')}</span>
                </button>

                <button
                  className="header__toggler"
                  onClick={(event) => {
                    document.documentElement.classList.toggle('nav--toggled')
                  }}
                  aria-controls="a11y-main-menu-collapse"
                  aria-expanded={isMenuOpen}
                >
                  <span className="sr-text">{t(`Header:Menu.${isMenuOpen ? 'Open' : 'Closed'}`)}</span>
                  <span aria-hidden="true"></span>
                  <span aria-hidden="true"></span>
                  <span aria-hidden="true"></span>
                </button>

                <button
                  className="header__search"
                  onClick={(event) => {
                    toggleSearchNav(event)
                  }}
                  aria-controls="a11y-search-collapse"
                  aria-expanded="false"
                >
                  <span className="sr-text">search.open</span>
                  <svg className="header__search-icon" height="22" width="22">
                    <use xlinkHref="#icon--search" />
                  </svg>
                </button>
              </div>
            </div>

            <div
              className={`languages languages--${
                state.countriesNav == 'open' || (regionCookie == '' && document.location.href == oldSites.get('en'))
                  ? 'visible'
                  : 'hidden'
              }`}
            >
              <div className="container container--m grid--gutter">
                <header className="languages__header">
                  <button className="languages__toggler" onClick={(event) => toggleCountriesNav(event)}>
                    <span className="sr-text">Close overlay</span>
                    <img className="languages__logo" src="/images/icons/icon--close--orange.svg" alt="" height="30" width="32"/>
                  </button>
                  <div className="header__logo">
                    <img className="languages__logo" src="/images/logo-new-refresco.svg" alt="Refresco logo" width="227" height="77" />
                  </div>
                </header>
                <div className="languages__inner container--s">
                  <div className="languages__message">
                    <p className="languages__intro">{t('header:visiting_current_page_intro')}</p>
                    <button
                      className="languages__cta button button--primary"
                      onClick={(e) => {
                        setCookie('locale', defaultLanguage, 1000)
                        switchLanguageDatalayer(localeTranslations.get(defaultLanguage), window.location.pathname)
                        const selectedRegionhref = `/global/`
                        let url = oldSites.get(selectedRegionhref.replace(/\//g, '') as any)
                        if (getLangFromUrl() != 'en' && getLangFromUrl() != 'global') {
                          url += '?clearcookie=true'
                        }
                        selectRegion(url)
                      }}
                    >
                      {t('header:link_to_refresco_group')}
                    </button>
                  </div>
                  <nav className="languages__nav nav nav--countries">
                    <ul className="nav__list">
                      {getRegions()
                        .filter((r) => r.languages.length > 0)
                        .map((region, count) => {
                          const defaultRegionLanguage = region.languages[0]
                          const restLanguages = region.languages.splice(1)
                          const currentLang = getLangFromUrl()
                          const regionTitle = region.translations?.[currentLang] || region.title
                          return (
                            <li className="nav__item" key={`region-selector-${count}-${defaultRegionLanguage.locale}`}>
                              <div className="nav__header">
                                <p className="nav__title text--l">{regionTitle}</p>
                                {region.flags && region.flags.length > 0 && (
                                  <div className="nav__flags">
                                    {region.flags.map((flag) => (
                                      <svg className="nav__flag" aria-hidden="true" width="26" height="20">
                                        <use xlinkHref={flag} />
                                      </svg>
                                    ))}
                                  </div>
                                )}
                              </div>

                              <div className="nav__languages">
                                <button
                                  className={`nav__link nav__link--${defaultRegionLanguage.locale}`}
                                  onClick={(e) => {
                                    if (defaultRegionLanguage.url) {
                                      window.open(defaultRegionLanguage.url, '_blank')
                                      return
                                    }
                                    const selectedRegionhref =
                                      defaultRegionLanguage.locale == 'global'
                                        ? `/global/`
                                        : `/${defaultRegionLanguage.locale}/`
                                    const key = selectedRegionhref.replace(/\//g, '') as any
                                    if (oldSites.has(key)) {
                                      setCookie(
                                        'locale',
                                        defaultRegionLanguage.locale == 'global' ? 'en' : defaultRegionLanguage.locale,
                                        1000
                                      )
                                      switchLanguageDatalayer(
                                        localeTranslations.get(defaultRegionLanguage.locale),
                                        window.location.pathname
                                      )
                                      const url = oldSites.get(key)
                                      // if(getLangFromUrl() != "en" && getLangFromUrl() != "global"){
                                      //   url += "?clearcookie=true"
                                      // }
                                      selectRegion(url)
                                    }
                                  }}
                                >
                                  <svg className="nav__icon" height="24" width="16">
                                    <use xlinkHref="#icon--arrow-left" />
                                  </svg>
                                  <span className="nav__language">{defaultRegionLanguage.name}</span>
                                </button>

                                {restLanguages.length > 0
                                  ? restLanguages.map((l) => (
                                      <button
                                        className={`nav__link nav__link--${defaultRegionLanguage.locale}`}
                                        onClick={(e) => {
                                          if (l.url) {
                                            window.open(l.url, '_blank')
                                            return
                                          }
                                          if (region.url) {
                                            window.open(region.url, '_blank')
                                            return
                                          }
                                          const selectedRegionhref = l.locale == 'global' ? `/en/` : `/${l.locale}/`
                                          const key = selectedRegionhref.replace(/\//g, '') as any
                                          if (oldSites.has(key)) {
                                            setCookie('locale', l.locale == 'global' ? 'global' : l.locale, 1000)
                                            switchLanguageDatalayer(
                                              localeTranslations.get(l.locale),
                                              window.location.pathname
                                            )
                                            const url = oldSites.get(key)
                                            selectRegion(url)
                                          }
                                        }}
                                      >
                                        <svg className="nav__icon" height="24" width="16">
                                          <use xlinkHref="#icon--arrow-left" />
                                        </svg>
                                        <span className="nav__language">{l.name}</span>
                                      </button>
                                    ))
                                  : null}
                              </div>
                            </li>
                          )
                        })}
                    </ul>
                  </nav>
                </div>
              </div>
            </div>

            <div
              className={`search search--${state.searchNav == 'open' ? 'show' : 'hidden'}`}
              key={`search-${state.searchNav}`}
            >
              <div className="search__inner container container--m grid--gutter">
                <form
                  onSubmit={(e) => {
                    e.preventDefault()
                    location.href = `/${state.currentLanguage}/search/${state.searchQuery}/0`
                  }}
                  className="search__form"
                >
                  <input
                    autoFocus
                    type="search"
                    value={state.searchQuery ?? ''}
                    onChange={(e) => {
                      const value = e.currentTarget.value
                      setState((state): HeaderState => ({ ...state, searchQuery: value }))
                    }}
                    className="search__input"
                  />
                </form>
                <button className="search__button" onClick={toggleSearchNav}>
                  <span className="sr-text">Closing search's button</span>
                </button>
              </div>
            </div>
          </StickyHeader>
        </>
      )
    })

const selectRegion = (url: string) => {
  setCookie('selectedRegion', url, 14)
  document.location.href = url
}

const getRegions = (): Region[] => [
  {
    title: 'Australia',
    languages: [{ name: 'English', locale: 'au' }],
    flags: ['#flag--au']
  },
  {
    title: 'Benelux',
    languages: [{ name: 'Nederlands', locale: 'nl' }],
    flags: ['#flag--be', '#flag--nl', '#flag--lu']
  },
  {
    title: 'Finland',
    languages: [{ name: 'Suomi', locale: 'fi' }],
    flags: ['#flag--fi']
  },
  {
    title: 'France',
    languages: [{ name: 'Français', locale: 'fr' }],
    flags: ['#flag--fr']
  },
  {
    title: 'Germany',
    languages: [{ name: 'Deutsch', locale: 'de' }],
    flags: ['#flag--de']
  },
  {
    title: 'Iberia',
    languages: [{ name: 'Español', locale: 'ib' }],
    flags: ['#flag--es']
  },
  {
    title: 'Italy',
    languages: [{ name: 'Italiano', locale: 'it' }],
    flags: ['#flag--it']
  },
  {
    title: 'North America',
    languages: [
      { name: 'English', locale: 'na' },
      { name: 'Français', locale: 'na-fr' },
      { name: 'SunOpta', locale: 'en', url: 'https://www.sunopta.com/' }
    ],
    flags: ['#flag--us', '#flag--ca', '#flag--mx']
  },
  {
    title: 'Norway',
    languages: [
      { name: 'English', locale: 'en', url: 'https://tkv.no/en/' },
      { name: 'Norwegian', locale: 'no', url: 'https://tkv.no/' }
    ],
    flags: ['#flag--no'],
    translations: {
      uk: 'Norway',
      fr: 'Norvège',
      es: 'Noruega',
      nl: 'Noorwegen',
      de: 'Norwegen',
      pl: 'Norwegia',
      it: 'Norvegia',
      fi: 'Norja'
    }
  },
  {
    title: 'Poland',
    languages: [{ name: 'Polski', locale: 'pl' }],
    flags: ['#flag--pl']
  },
  {
    title: 'United Kingdom',
    languages: [{ name: 'English', locale: 'uk' }],
    flags: ['#flag--uk']
  }
]

type Region = { title: string; languages: RegionLanguage[]; flags: string[]; url?: string; translations?: Record<string, string> }

type RegionLanguage = { name: string; locale: Locale; url?: string }
